import React, { useEffect, useRef, useState } from 'react';
import SvgIcon from '@/components/SvgIcon';
import { I_LEFT_BRACKET, I_RIGHT_BRACKET } from '@/types/images';

interface ImageViewerModalProps {
    images: string[];
    initialIndex: number;
    isOpen: boolean;
    onClose: () => void;
}

const ImageViewerModal = ({ images, initialIndex, isOpen, onClose }: ImageViewerModalProps) => {
    const [currentIndex, setCurrentIndex] = useState(initialIndex);
    const closeButtonRef = useRef<HTMLButtonElement>(null);

    // Sync state when opening with a new index
    useEffect(() => {
        if (isOpen) {
            setCurrentIndex(initialIndex);
        }
    }, [isOpen, initialIndex]);

    // Handle body overflow
    useEffect(() => {
        if (!isOpen) return;

        const previouslyFocused = document.activeElement as HTMLElement | null;
        const originalStyle = window.getComputedStyle(document.body).overflow;
        // Only modify if it's not already hidden (e.g. by another modal)
        if (originalStyle !== 'hidden') {
            document.body.style.overflow = 'hidden';
        }
        closeButtonRef.current?.focus();

        return () => {
            if (originalStyle !== 'hidden') {
                document.body.style.overflow = originalStyle;
            }
            previouslyFocused?.focus();
        };
    }, [isOpen]);

    const handlePrev = React.useCallback(() => {
        setCurrentIndex((prev) => (prev > 0 ? prev - 1 : prev));
    }, []);

    const handleNext = React.useCallback(() => {
        setCurrentIndex((prev) => (prev < images.length - 1 ? prev + 1 : prev));
    }, [images.length]);

    // Keyboard navigation
    useEffect(() => {
        if (!isOpen) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
            if (e.key === 'ArrowLeft') handlePrev();
            if (e.key === 'ArrowRight') handleNext();
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, handlePrev, handleNext, onClose]);

    if (!isOpen) return null;

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-label="이미지 크게 보기"
            className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center backdrop-blur-sm"
        >
            {/* Close Button */}
            <button
                ref={closeButtonRef}
                onClick={onClose}
                aria-label="이미지 뷰어 닫기"
                className="absolute top-10 right-10 p-2 rounded-full hover:bg-white/10 transition-colors z-[101]"
            >
                <SvgIcon name="SvgX" size={40} stroke="#FFFFFF" />
            </button>

            {/* Navigation Left */}
            <button
                onClick={handlePrev}
                disabled={currentIndex === 0}
                aria-label="이전 이미지"
                className={`absolute left-10 p-4 transition-all z-[101] ${
                    currentIndex === 0
                        ? 'opacity-30 cursor-not-allowed'
                        : 'opacity-100 hover:bg-white/10 rounded-full hover:scale-110'
                }`}
            >
                <img src={I_LEFT_BRACKET} className="w-8 h-[58px]" alt="Previous" />
            </button>

            {/* Main Image Container */}
            <div className="w-full h-full flex items-center justify-center p-20 select-none">
                <img
                    src={images[currentIndex]}
                    alt={`확대 이미지 ${currentIndex + 1}`}
                    className="max-w-full max-h-[85vh] object-contain shadow-2xl"
                    draggable={false}
                />
            </div>

            {/* Navigation Right */}
            <button
                onClick={handleNext}
                disabled={currentIndex === images.length - 1}
                aria-label="다음 이미지"
                className={`absolute right-10 p-4 transition-all z-[101] ${
                    currentIndex === images.length - 1
                        ? 'opacity-30 cursor-not-allowed'
                        : 'opacity-100 hover:bg-white/10 rounded-full hover:scale-110'
                }`}
            >
                <img src={I_RIGHT_BRACKET} className="w-8 h-[58px]" alt="Next" />
            </button>

            {/* Image Counter */}
            <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 text-white/90 text-lg font-medium px-4 py-2 bg-black/50 rounded-full">
                {currentIndex + 1} / {images.length}
            </div>

            {/* Thumbnail Strip (Optional, minimal version) */}
            {images.length > 1 && (
                <div className="absolute bottom-24 left-1/2 transform -translate-x-1/2 flex gap-2 max-w-[80vw] overflow-x-auto pb-2 scrollbar-hide">
                    {images.map((img, idx) => (
                        <button
                            key={idx}
                            onClick={() => setCurrentIndex(idx)}
                            aria-label={`이미지 ${idx + 1} 보기`}
                            aria-pressed={idx === currentIndex}
                            className={`relative min-w-[60px] h-[60px] rounded-lg overflow-hidden border-2 transition-all ${
                                idx === currentIndex
                                    ? 'border-primary scale-110 z-10'
                                    : 'border-transparent opacity-60 hover:opacity-100'
                            }`}
                        >
                            <img src={img} alt="" className="w-full h-full object-cover" />
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ImageViewerModal;
